import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Header, Loader, Success, Failed } from '../../../custom/components';
import { useNavigate } from 'react-router-dom';
import { customStyles } from '../../../custom/Utils/utils';
import { EditorContent, useEditor } from '@tiptap/react';
import './styles.scss';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../custom/store/store';
import { getHospitalId } from '../../../custom/Utils/authUtils';
import { createReportTemplateApi } from '../../rtk-apis/reports/createTemplate';
import { clearState } from '../../store/slices/reports/createReportTemplate';
import { patientDemographicTable } from './patientDemographicsTable';
import { extensions } from '../../../custom/Utils/reportExtension';
import TemplateNameModal from './templateNameModal';
import Placeholder from '@tiptap/extension-placeholder';
import * as mammoth from 'mammoth';
import InsertDynamicFieldModal, {
  InsertDynamicFieldResult,
  DynamicFieldAutoFillSource,
} from '../../../custom/components/InsertDynamicFieldModal';
import { generateUniqueDynamicFieldKey } from '../../../custom/Utils/dynamicFieldKeys';
import { wrapDynamicField, DynamicFieldType } from '../../../custom/Utils/dynamicFieldMarkup';
import { findChainOccurrences } from '../../../custom/Utils/chainFieldSync';
import { applyDynamicFieldMetaUpdate } from '../../../custom/Utils/dynamicFieldSync';
import { CHAIN_FIELDS } from '../../../custom/Utils/measurementChain';
import type { DynamicFieldEditValues } from '../../../custom/components/InsertDynamicFieldModal';
import TemplateEditorToolbar from '../../../custom/components/TemplateEditorToolbar';
const UploadTemplate = () => {
  const importRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);
  const [displayMessage, setDisplayMessage] = useState('');
  const [isTemplateNameModalOpen, setIsTemplateNameModalOpen] = useState(false);
  const [placeholderApplied, setPlaceholderApplied] = useState(false);
  const [isInsertFieldModalOpen, setIsInsertFieldModalOpen] = useState(false);
  /** Set when Insert Field is opened by clicking an EXISTING dynamic field, to edit it in place. */
  const [editingDynamicField, setEditingDynamicField] = useState<{
    fieldKey: string;
    values: DynamicFieldEditValues;
  } | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const createReportTemplateResponse = useSelector(
    (state: RootState) => state.createReportTemplateResponse
  );
  const navigate = useNavigate();

  const initialContent = `${patientDemographicTable}<p></p>`; // Simplified initial content

  const editor = useEditor({
    extensions: [
      ...extensions,
      Placeholder.configure({
        placeholder: ({ editor, node }) => {
          const { doc } = editor.state;
          const paragraphs = doc.content.content;

          let foundNonEmpty = false;
          let firstEmptyParagraph = null;

          for (let i = 0; i < paragraphs.length; i++) {
            const para = paragraphs[i];

            if (para.textContent.trim() !== '') {
              foundNonEmpty = true;
            } else if (foundNonEmpty && !firstEmptyParagraph) {
              firstEmptyParagraph = para;
            }
          }

          // Apply placeholder only if this is the first empty paragraph AND it hasn't been applied before
          if (!placeholderApplied && firstEmptyParagraph && node === firstEmptyParagraph) {
            return 'Write something …';
          }

          return ''; // No placeholder for new empty paragraphs
        },
      }),
    ],
    content: initialContent,
    onUpdate: () => {
      setPlaceholderApplied(true); // Prevent further placeholders after typing
    },
    editorProps: {
      handleClick: (view, pos, event: any) => {
        const dynamicFieldTarget = event.target.closest?.('span[data-dynamic-field]');
        if (!dynamicFieldTarget) {
          return false;
        }
        event.stopPropagation();
        const key = dynamicFieldTarget.classList[0];
        if (!key) {
          return true;
        }
        const label = dynamicFieldTarget.getAttribute('title') || key;
        const dynFieldType =
          (dynamicFieldTarget.getAttribute('data-field-type') as DynamicFieldType | null) || 'text';
        let dynOptions: string[] = [];
        const rawOptions = dynamicFieldTarget.getAttribute('data-options');
        if (rawOptions) {
          try {
            dynOptions = JSON.parse(rawOptions);
          } catch {
            dynOptions = [];
          }
        }
        setEditingDynamicField({
          fieldKey: key,
          values: {
            label,
            fieldType: dynFieldType,
            options: dynOptions,
            unit: dynamicFieldTarget.getAttribute('data-unit') || undefined,
            srConcept: dynamicFieldTarget.getAttribute('data-sr-concept') || undefined,
          },
        });
        setIsInsertFieldModalOpen(true);
        return true;
      },
    },
  });

  // Auto-focus on the first empty paragraph after patientDemographicTable
  useEffect(() => {
    if (editor) {
      const { doc } = editor.state;
      let foundNonEmpty = false;
      let firstEmptyPos = null;

      doc.descendants((node, pos) => {
        if (node.type.name === 'paragraph') {
          if (node.textContent.trim() !== '') {
            foundNonEmpty = true;
          } else if (foundNonEmpty && firstEmptyPos === null) {
            firstEmptyPos = pos + 1; // Get position of first empty paragraph
          }
        }
      });

      if (firstEmptyPos !== null) {
        editor.commands.setTextSelection(firstEmptyPos);
        editor.commands.focus();
      }
    }
  }, [editor]);

  const handleImportClick = useCallback(() => {
    importRef.current?.click();
  }, []);

  const handleImportFilePick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        return;
      }

      setIsLoading(true);
      setError(null);

      const fileType = file.name.split('.').pop()?.toLowerCase();
      // Common HTML processing function
      const processHtmlContent = (html: string): string => {
        return html
          .replace(/<table\b/g, '<table class="table-bordered"') // Add table-bordered class
          .replace(/<u\b/g, '<span style="text-decoration:underline"') // Convert <u> to styled span
          .replace(/<\/u>/g, '</span>'); // Close the span
      };
      if (fileType === 'html') {
        const reader = new FileReader();
        reader.onload = event => {
          const content = event.target?.result as string;
          const processedHtml = processHtmlContent(content);
          editor?.commands.setContent(`${patientDemographicTable}${processedHtml}`);
          setIsLoading(false);
        };
        reader.onerror = () => {
          setError('Failed to read the HTML file');
          setIsLoading(false);
        };
        reader.readAsText(file);
      } else if (fileType === 'docx') {
        const reader = new FileReader();
        reader.onload = async event => {
          try {
            const arrayBuffer = event.target?.result as ArrayBuffer;
            // Convert DOCX to HTML
            const result = await mammoth.convertToHtml(
              { arrayBuffer },
              {
                styleMap: [
                  "u => span[style='text-decoration:underline']",
                  "p[style-name='Horizontal Line'] => hr",
                  "p[style-name='Footer'] => hr",
                  "p[style-name='Header'] => hr",
                  "p[style-name='Normal'] => p",
                  // Custom table mapping to preserve structure
                  'table => table.table-bordered',
                  'tr => tr',
                  'td => td',
                  'th => th',
                ],
                convertImage: mammoth.images.imgElement(async image => {
                  const imageBuffer = await image.read('base64');
                  const contentType = image.contentType || 'image/png';
                  const width = image.width ? `width="${image.width}px"` : '';
                  const height = image.height ? `height="${image.height}px"` : '';
                  const imageId = `img-${Math.random().toString(36).substr(2, 9)}`;
                  return {
                    src: `data:${contentType};base64,${imageBuffer}`,
                    id: imageId,
                    alt: 'Document Image',
                    style: 'max-width: 100%; height: auto;',
                    ...(width && { width }),
                    ...(height && { height }),
                  };
                }),
                includeEmbeddedStyleMap: true,
                includeDefaultStyleMap: true,
                preserveEmptyParagraphs: true,
              }
            );

            // Process HTML
            const processedHtml = result.value
              .replace(/ {2}/g, '  ')
              .replace(/\t/g, '    ')
              .replace(/\n/g, '<br/>');

            editor?.commands.setContent(`${patientDemographicTable}${processedHtml}`);
          } catch (err) {
            setError('Failed to process the Word document');
            console.error('DOCX conversion error:', err);
          } finally {
            setIsLoading(false);
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        setError('Unsupported file type. Please upload an HTML file.');
        setIsLoading(false);
      }

      if (importRef.current) {
        importRef.current.value = '';
      }
    },
    [editor]
  );

  const handleInsertDynamicField = useCallback(
    ({ fieldKey, label, fieldType, options, unit, initialValue, srConcept }: InsertDynamicFieldResult) => {
      if (!editor) {
        return;
      }
      if (editingDynamicField) {
        applyDynamicFieldMetaUpdate(editor, fieldKey, { label, fieldType, options, unit, srConcept });
        setEditingDynamicField(null);
        setIsInsertFieldModalOpen(false);
        return;
      }
      editor
        .chain()
        .focus()
        .insertContent(
          wrapDynamicField(fieldKey, label, initialValue || '', { fieldType, options, unit, srConcept })
        )
        .run();
      setIsInsertFieldModalOpen(false);
    },
    [editor, editingDynamicField]
  );

  const getAutoFillSources = useCallback((): DynamicFieldAutoFillSource[] => {
    if (!editor) {
      return [];
    }
    const seen = new Map<string, DynamicFieldAutoFillSource>();
    findChainOccurrences(editor).forEach(({ key, text }) => {
      if (seen.has(key) || !text.trim()) {
        return;
      }
      const spec = CHAIN_FIELDS[key];
      if (!spec) {
        return;
      }
      seen.set(key, { key, label: spec.label, value: text });
    });
    return Array.from(seen.values());
  }, [editor]);

  const handleSave = () => {
    if (editor) {
      setIsTemplateNameModalOpen(true);
    }
  };

  const handleSaveConfirm = (name: string) => {
    if (editor) {
      const title = `<h2 data-class="report-title">${name}</h2>`;
      const htmlContent = title + editor.getHTML();

      const payload = {
        templateName: name,
        templateHTML: htmlContent,
        hospitalId: getHospitalId(),
        modality: 'US',
        editorMode: 'default',
        jsonData: {},
      };
      dispatch(createReportTemplateApi(payload));
      setIsTemplateNameModalOpen(false);
    }
  };

  const handleSaveCancel = () => {
    setIsTemplateNameModalOpen(false);
  };

  useEffect(() => {
    const handleSuccess = (message: string) => {
      setDisplayMessage(message || 'Template created successfully');
      setIsSuccessModalOpen(true);
      setTimeout(() => {
        setIsSuccessModalOpen(false);
        setDisplayMessage('');
        editor?.commands.setContent(initialContent);
        navigate('/templates');
      }, 2500);
    };

    const handleFailure = (message: string) => {
      setDisplayMessage(message || 'Failed to create template');
      setIsFailedModalOpen(true);
      setTimeout(() => {
        setIsFailedModalOpen(false);
        setDisplayMessage('');
      }, 1500);
    };

    if (createReportTemplateResponse.status === 'SUCCESS') {
      handleSuccess(createReportTemplateResponse.msg);
    } else if (createReportTemplateResponse.status === 'FAILED') {
      handleFailure(createReportTemplateResponse.msg);
    }

    return () => {
      dispatch(clearState());
    };
  }, [
    createReportTemplateResponse.status,
    createReportTemplateResponse.msg,
    editor,
    dispatch,
    navigate,
  ]);

  if (!editor) {
    return <div>Loading editor...</div>;
  }

  return (
    <section
      className="layoutContainer"
      style={customStyles.layoutBackground}
    >
      <div className="layoutGrid">
        <div className="page">
          <Header />
          <div className="page__body">
            <div className="control-group">
              <div className="button-group">
                <button onClick={handleImportClick}>
                  Import Template
                  <input
                    onChange={handleImportFilePick}
                    type="file"
                    accept=".html,.docx"
                    ref={importRef}
                    style={{ display: 'none' }}
                  />
                </button>
                <button
                  onClick={() => {
                    setEditingDynamicField(null);
                    setIsInsertFieldModalOpen(true);
                  }}
                >
                  Insert Field
                </button>
              </div>
              {isLoading && <div className="hint purple-spinner">Processing...</div>}
              {error && <div className="hint error">{error}</div>}
            </div>
            <TemplateEditorToolbar editor={editor} />
            <div
              className="whiteTemplateBox"
              style={{ position: 'relative' }}
            >
              <EditorContent editor={editor} />
            </div>
            <button
              className="btn btn__primary mt-4"
              onClick={handleSave}
            >
              Save Template
            </button>
          </div>
        </div>
      </div>
      <Loader isLoading={isLoading || createReportTemplateResponse.loading} />
      <Success
        handleCloseModal={() => setIsSuccessModalOpen(false)}
        title={displayMessage}
        isSuccessModalOpen={isSuccessModalOpen}
      />
      <Failed
        handleCloseModal={() => setIsFailedModalOpen(false)}
        title={displayMessage}
        isFailedModalOpen={isFailedModalOpen}
      />
      <TemplateNameModal
        isOpen={isTemplateNameModalOpen}
        onConfirm={handleSaveConfirm}
        onCancel={handleSaveCancel}
      />
      <InsertDynamicFieldModal
        isOpen={isInsertFieldModalOpen}
        onCancel={() => {
          setIsInsertFieldModalOpen(false);
          setEditingDynamicField(null);
        }}
        onConfirm={handleInsertDynamicField}
        previewFieldKey={label => (editor ? generateUniqueDynamicFieldKey(editor, label) : '')}
        autoFillSources={getAutoFillSources()}
        editingFieldKey={editingDynamicField?.fieldKey}
        editingValues={editingDynamicField?.values}
      />
    </section>
  );
};

export default UploadTemplate;
